import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import dayjs from "dayjs";
import { User, FileText, Upload, Loader2, FileDown, Briefcase } from "lucide-react";
import Navbar from "../components/Navbar";
import StatusBadge from "../components/StatusBadge";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const inputClass = "w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400";
const labelClass = "text-xs font-bold uppercase tracking-wider text-slate-600";

function FileUploadBox({ kind, currentName, currentPath, onUploaded, t }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("candidate.uploadSuccess"));
      onUploaded();
    } catch (err) {
      toast.error(formatApiError(err, t("candidate.uploadError")));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="rounded-md border-2 border-slate-900 bg-slate-50 p-5" data-testid={`upload-box-${kind}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md border-2 border-slate-900 bg-sky-100">
            <FileText className="h-5 w-5 text-slate-900" strokeWidth={2} />
          </span>
          <div>
            <p className="text-sm font-bold text-slate-900">{kind === "cv" ? t("candidate.cv") : t("candidate.coverLetter")}</p>
            {currentName ? (
              <a href={`${apiBase}/files/${currentPath}`} target="_blank" rel="noreferrer" data-testid={`download-${kind}`}
                className="flex items-center gap-1 text-xs font-semibold text-sky-600 hover:underline">
                <FileDown className="h-3 w-3" /> {currentName}
              </a>
            ) : (
              <p className="text-xs text-slate-400">PDF, DOC, DOCX — 10 Mo max</p>
            )}
          </div>
        </div>
        <button
          type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid={`upload-${kind}-button`}
          className="flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-4 py-2 text-xs font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-100 disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" strokeWidth={2} />}
          {uploading ? t("candidate.uploading") : kind === "cv" ? t("candidate.uploadCv") : t("candidate.uploadCoverLetter")}
        </button>
        <input ref={inputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFile} data-testid={`upload-${kind}-input`} />
      </div>
    </div>
  );
}

export default function CandidateDashboard() {
  const { t, lang } = useLanguage();
  const { user, checkAuth } = useAuth();
  const [tab, setTab] = useState("profile");
  const [applications, setApplications] = useState([]);
  const [examples, setExamples] = useState([]);
  const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";
  const [loadingApps, setLoadingApps] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({ name: "", title: "", skills: "", experience: "", location: "", phone: "", bio: "" });
  const locale = lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US";

  const fetchApplications = useCallback(async () => {
    setLoadingApps(true);
    try {
      const { data } = await api.get("/applications/mine");
      setApplications(data);
    } finally {
      setLoadingApps(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications().catch(() => {});
    api.get("/cv-examples").then(({ data }) => setExamples(data)).catch(() => {});
  }, [fetchApplications]);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "", title: user.title || "", skills: user.skills || "",
        experience: user.experience || "", location: user.location || "", phone: user.phone || "", bio: user.bio || "",
      });
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", profile);
      await checkAuth();
      toast.success(t("candidate.profileSaved"));
    } catch (err) {
      toast.error(formatApiError(err, t("common.error")));
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { key: "profile", label: t("candidate.profile"), icon: User },
    { key: "applications", label: t("candidate.myApplications"), icon: Briefcase },
    { key: "examples", label: t("candidate.cvExamples"), icon: FileDown },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="candidate-dashboard">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">{user?.name}</h1>
        <p className="mt-1 text-sm text-slate-500">{user?.title || user?.email}</p>

        <div className="mt-6 flex gap-2 border-b-2 border-slate-200" data-testid="candidate-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.key} onClick={() => setTab(tb.key)} data-testid={`candidate-tab-${tb.key}`}
              className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                tab === tb.key ? "border-sky-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <tb.icon className="h-4 w-4" strokeWidth={2} /> {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "profile" && (
            <div className="grid gap-6 lg:grid-cols-3">
              <form onSubmit={saveProfile} className="rounded-md border-2 border-slate-900 bg-white p-8 lg:col-span-2" data-testid="candidate-profile-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>{t("auth.name")}</label>
                    <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="candidate-name-input" className={`${inputClass} mt-1.5`} />
                  </div>
                  <div>
                    <label className={labelClass}>{t("candidate.jobTitleLabel")}</label>
                    <input value={profile.title} onChange={(e) => setProfile({ ...profile, title: e.target.value })} placeholder={t("candidate.jobTitlePlaceholder")} data-testid="candidate-title-input" className={`${inputClass} mt-1.5`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>{t("candidate.skills")}</label>
                    <input value={profile.skills} onChange={(e) => setProfile({ ...profile, skills: e.target.value })} placeholder={t("candidate.skillsPlaceholder")} data-testid="candidate-skills-input" className={`${inputClass} mt-1.5`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>{t("candidate.experience")}</label>
                    <input value={profile.experience} onChange={(e) => setProfile({ ...profile, experience: e.target.value })} placeholder={t("candidate.experiencePlaceholder")} data-testid="candidate-experience-input" className={`${inputClass} mt-1.5`} />
                  </div>
                  <div>
                    <label className={labelClass}>{t("common.location")}</label>
                    <input value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} data-testid="candidate-location-input" className={`${inputClass} mt-1.5`} />
                  </div>
                  <div>
                    <label className={labelClass}>{t("candidate.phone")}</label>
                    <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="candidate-phone-input" className={`${inputClass} mt-1.5`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>{t("candidate.bio")}</label>
                    <textarea rows={3} value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} placeholder={t("candidate.bioPlaceholder")} data-testid="candidate-bio-input" className={`${inputClass} mt-1.5`} />
                  </div>
                </div>
                <button type="submit" disabled={saving} data-testid="candidate-profile-save"
                  className="mt-6 flex items-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 px-6 py-2.5 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 disabled:opacity-60">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("company.save")}
                </button>
              </form>

              <div className="space-y-5">
                <FileUploadBox kind="cv" currentName={user?.cv_path_name} currentPath={user?.cv_path} onUploaded={checkAuth} t={t} />
                <FileUploadBox kind="cover_letter" currentName={user?.cover_letter_path_name} currentPath={user?.cover_letter_path} onUploaded={checkAuth} t={t} />
              </div>
            </div>
          )}

          {tab === "examples" && (
            <div>
              <p className="mb-5 text-sm text-slate-500">{t("candidate.cvExamplesDesc")}</p>
              {examples.length === 0 ? (
                <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-16 text-center text-sm text-slate-500" data-testid="no-cv-examples">
                  {t("candidate.cvExamplesEmpty")}
                </div>
              ) : (
                <div className="space-y-4" data-testid="cv-examples-list">
                  {examples.map((ex) => (
                    <div key={ex.file_id} className="flex flex-wrap items-center justify-between gap-4 rounded-md border-2 border-slate-900 bg-white p-5 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]" data-testid={`cv-example-${ex.file_id}`}>
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-md border-2 border-slate-900 bg-yellow-100">
                          <FileText className="h-5 w-5 text-slate-900" strokeWidth={2} />
                        </span>
                        <div>
                          <p className="font-heading text-base font-bold text-slate-900">{ex.title}</p>
                          <p className="text-xs text-slate-500">{ex.original_filename}</p>
                        </div>
                      </div>
                      <a href={`${apiBase}/files/${ex.storage_path}`} target="_blank" rel="noreferrer" data-testid={`download-example-${ex.file_id}`}
                        className="flex items-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 px-4 py-1.5 text-xs font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500">
                        <FileDown className="h-4 w-4" strokeWidth={2} /> {t("candidate.download")}
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "applications" && (
            <div>
              <p className="mb-5 text-sm text-slate-500">{t("candidate.applicationsSubtitle")}</p>
              {loadingApps ? (
                <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>
              ) : applications.length === 0 ? (
                <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-16 text-center" data-testid="no-applications-candidate">
                  <p className="text-sm text-slate-500">{t("candidate.noApplications")}</p>
                  <Link to="/jobs" data-testid="browse-jobs-link" className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 px-5 py-2 text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5">
                    {t("candidate.browseJobs")}
                  </Link>
                </div>
              ) : (
                <div className="space-y-4" data-testid="candidate-applications-list">
                  {applications.map((a) => (
                    <div key={a.application_id} className="flex flex-wrap items-center justify-between gap-4 rounded-md border-2 border-slate-900 bg-white p-5 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]" data-testid={`my-application-${a.application_id}`}>
                      <div>
                        <p className="font-heading text-base font-bold text-slate-900">{a.job_title}</p>
                        <p className="text-sm text-slate-500">{a.company_name} · {t("candidate.appliedOn")} {dayjs(a.created_at).locale(locale).format("DD/MM/YYYY")}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={a.status} t={t} />
                        <Link to={`/jobs/${a.job_id}`} data-testid={`view-job-${a.application_id}`}
                          className="rounded-full border-2 border-slate-900 bg-white px-4 py-1.5 text-xs font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-100">
                          {t("candidate.viewJob")}
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
